import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface DailyStat {
    date: string;
    count: number;
    success?: number;
    failed?: number;
}

interface UsageChartProps {
    data: DailyStat[];
    loading?: boolean;
}

export function UsageChart({ data, loading }: UsageChartProps) {
    const [filter, setFilter] = useState<'all' | 'success' | 'failed'>('all');

    if (loading) {
        return (
            <div className="h-[200px] w-full flex items-center justify-center bg-muted/10 rounded-lg animate-pulse">
                <span className="text-sm text-muted-foreground">Generating Analytics...</span>
            </div>
        );
    }

    if (!data || data.length === 0) return null;

    // Format date for better display (e.g., Feb 24)
    const formattedData = data.map(item => ({
        ...item,
        displayDate: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }));

    const dataKey = filter === 'all' ? 'count' : filter;

    // Choose colors based on filter
    const getColors = () => {
        if (filter === 'success') return { stroke: '#22c55e', stop: '#22c55e' }; // green-500
        if (filter === 'failed') return { stroke: '#ef4444', stop: '#ef4444' };   // red-500
        return { stroke: 'hsl(var(--primary))', stop: 'hsl(var(--primary))' };
    };
    const colors = getColors();

    return (
        <div className="w-full mt-2">
            <div className="flex items-center justify-between mb-4">
                <span className="text-xs text-muted-foreground">Detailed breakdown of your API traffic.</span>
                <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value as any)}
                    className="text-xs bg-background border border-border rounded-md px-3 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-primary shadow-sm"
                >
                    <option value="all">Total Requests</option>
                    <option value="success">Successful</option>
                    <option value="failed">Failed</option>
                </select>
            </div>

            <div className="h-[240px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={formattedData}>
                        <defs>
                            <linearGradient id="colorMetric" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={colors.stop} stopOpacity={0.3} />
                                <stop offset="95%" stopColor={colors.stop} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                        <XAxis
                            dataKey="displayDate"
                            fontSize={10}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: 'hsl(var(--muted-foreground))' }}
                            interval={4}
                        />
                        <YAxis
                            fontSize={10}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: 'hsl(var(--muted-foreground))' }}
                        />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: 'hsl(var(--background))',
                                borderColor: 'hsl(var(--border))',
                                borderRadius: '8px',
                                fontSize: '12px',
                                color: 'hsl(var(--foreground))'
                            }}
                            itemStyle={{ color: colors.stroke, fontWeight: 'bold' }}
                            cursor={{ stroke: colors.stroke, strokeWidth: 1 }}
                        />
                        <Area
                            type="monotone"
                            dataKey={dataKey}
                            stroke={colors.stroke}
                            fillOpacity={1}
                            fill="url(#colorMetric)"
                            strokeWidth={2}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
